<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import SidebarNavItem from '@/components/layout/sidebar/SidebarNavItem.vue'
import SidebarSectionDivider from '@/components/layout/sidebar/SidebarSectionDivider.vue'
import SidebarEventSwitcher from '@/components/layout/sidebar/SidebarEventSwitcher.vue'

const props = defineProps<{ eventSlug: string }>()
const emit = defineEmits<{ (e: 'navigate'): void }>()

const { t } = useI18n()
const { fetchEvent } = useEvents()

onMounted(() => void fetchEvent(props.eventSlug))

const LAYERS_ICON = [
  'M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122',
]

const TAG_ICON = [
  'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z',
  'M6 6h.008v.008H6V6z',
]

const COG_ICON = [
  'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z',
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
]

const PHOTO_ICON = [
  'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
]

const GLOBE_ICON = [
  'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
]
</script>

<template>
  <div class="flex flex-col">
    <!-- Event switcher -->
    <SidebarEventSwitcher :current-event-slug="props.eventSlug" @navigate="emit('navigate')" />

    <!-- Back link -->
    <div class="px-4 py-3 border-b border-slate-800">
      <RouterLink
        to="/organizer/events"
        class="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        @click="emit('navigate')"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('nav.sidebar.back_to_events') }}
      </RouterLink>
    </div>

    <nav class="flex flex-col py-3" aria-label="Event navigation">
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}`"
        :label="t('nav.sidebar.editions')"
        :exact-active="true"
        :icon="LAYERS_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/vouchers`"
        :label="t('nav.sidebar.vouchers')"
        :icon="TAG_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/settings`"
        :label="t('nav.sidebar.settings')"
        :icon="COG_ICON"
        @navigate="emit('navigate')"
      />

      <SidebarSectionDivider />

      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/public-website`"
        :label="t('nav.sidebar.public_website')"
        :icon="GLOBE_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/galleries`"
        :label="t('nav.sidebar.galleries')"
        :icon="PHOTO_ICON"
        @navigate="emit('navigate')"
      />
    </nav>
  </div>
</template>
